import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { BehaviorSubject, filter, map, Observable, take } from 'rxjs';
import { AuthService } from './auth.service';
import { environment } from '../../../environments/environment';
import { EmployeeData, UserData, UserCompany, ChangePasswordData, CreateCompanyRequest, Response } from '../interfaces';
import { EMPLOYEE_ROLES } from '../enums';

@Injectable({
  providedIn: 'root'
})
export class UserService {
  profileInfo$: BehaviorSubject<EmployeeData> = new BehaviorSubject<EmployeeData>({} as EmployeeData);
  userInfo$: BehaviorSubject<UserData | null> = new BehaviorSubject<UserData | null>(null);
  userCompanyList$: BehaviorSubject<UserCompany[] | null> = new BehaviorSubject<UserCompany[] | null>(null);
  currentUserRoles$: BehaviorSubject<EMPLOYEE_ROLES[]> = new BehaviorSubject<EMPLOYEE_ROLES[]>([]);

  isProfileUpToDate$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  isUserInfoUpToDate$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  isUserCompanyListUpToDate$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

  constructor(
    private readonly http: HttpClient,
    private readonly authService: AuthService
  ) {
    this.authService.isLoggedIn().subscribe({
      next: (isLoggedIn) => {
        if (isLoggedIn) {
          if (!this.isUserCompanyListUpToDate$.value) {
            this.getUserCompanies();
          }
          if (!this.isUserInfoUpToDate$.value) {
            this.getUserInfo();
          }
        }
      }
    });

    this.authService.companyChanged$.subscribe((companyId) => {
      if (companyId) {
        this.getProfileInfoData();
      }
    });
  }

  getUserCompanyById(companyId: number): Observable<UserCompany | undefined> {
    return this.userCompanyList$.pipe(
      filter((companyList) => companyList !== null),
      map((companyList) => companyList?.find((company) => company.id === companyId)),
      take(1)
    );
  }

  getProfileInfoData(): void {
    this.getProfileInfoRequest().subscribe({
      next: (response) => {
        if (response.success && response.data) {
          this.profileInfo$.next(response.data);
          this.currentUserRoles$.next(response.data.roles ?? []);
          this.isProfileUpToDate$.next(true);
        }
      }
    });
  }

  getUserCompanies(): void {
    this.getUserCompaniesRequest().subscribe({
      next: (response) => {
        if (response.success && response.data) {
          this.isUserCompanyListUpToDate$.next(true);
          this.userCompanyList$.next(response.data);
        }
      }
    });
  }

  getUserInfo(): void {
    this.getUserInfoRequest().subscribe({
      next: (response) => {
        if (response.success && response.data) {
          this.isUserInfoUpToDate$.next(true);
          this.userInfo$.next(response.data);
        }
      }
    });
  }

  checkUserRights(userRoles: EMPLOYEE_ROLES[]) {
    return userRoles.some((roleToCheck: EMPLOYEE_ROLES) => this.currentUserRoles$.getValue().includes(roleToCheck));
  }

  getProfileInfoRequest(): Observable<Response<EmployeeData>> {
    return this.http.get<Response<EmployeeData>>(`${environment.baseUrl}/employees/me`);
  }

  getUserCompaniesRequest(): Observable<Response<UserCompany[]>> {
    return this.http.get<Response<UserCompany[]>>(`${environment.baseUrl}/users/companies`);
  }

  getUserInfoRequest(): Observable<Response<any>> {
    return this.http.get<Response<any>>(`${environment.baseUrl}/users/me`);
  }

  updateUserInfoRequest(data: UserData): Observable<Response<UserData>> {
    return this.http.put<Response<UserData>>(`${environment.baseUrl}/users/me`, data);
  }

  unlinkGoogleAccountRequest(): Observable<Response<any>> {
    return this.http.post<Response<any>>(`${environment.baseUrl}/auth/account/unlink/google`, {});
  }

  changePasswordRequest(data: ChangePasswordData): Observable<Response<any>> {
    return this.http.post<Response<any>>(`${environment.baseUrl}/auth/account/password/change`, data);
  }

  createCompanyRequest(data: CreateCompanyRequest, file: File | null = null): Observable<Response<UserCompany>> {
    const formData = new FormData();
    const jsonBlob = new Blob([JSON.stringify(data)], { type: 'application/json' });

    formData.append('request', jsonBlob);
    if (file) {
      formData.append('logo', file);
    }

    const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

    return this.http.post<Response<UserCompany>>(`${environment.baseUrl}/company`, formData, {
      headers: new HttpHeaders({
        'X-Timezone': timeZone
      })
    });
  }

  clearData() {
    this.currentUserRoles$.next([]);
    this.profileInfo$.next({} as EmployeeData);
    this.isProfileUpToDate$.next(false);
    this.userInfo$.next(null);
    this.isUserInfoUpToDate$.next(false);
    this.userCompanyList$.next([]);
    this.isUserCompanyListUpToDate$.next(false);
  }
}
